import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAccessManagement } from '../hooks/useAccessManagement.js'
import { useAccessData } from '../hooks/useAccessData.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import LoadingSpinner from '../components/LoadingSpinner.jsx'

/**
 * AcceptInvitationPage - Handles device invitation acceptance from email links
 * 
 * URL: /invitations/:invitationId
 * Purpose: Allow users to accept device invitations via email link
 * Integration: Uses existing useAccessManagement hook for invitation handling
 */
function AcceptInvitationPage() {
  const { invitationId } = useParams()
  const navigate = useNavigate()
  const { user } = useCloudContext()
  const { acceptInvitation, isLoading } = useAccessManagement()
  const { getUserInfo } = useAccessData()
  
  const [state, setState] = useState({
    invitation: null,
    inviterName: '',
    loading: true,
    error: null,
    accepting: false,
    success: false,
  })

  // Load invitation details
  useEffect(() => {
    if (!invitationId || !user) return

    const loadInvitation = async () => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }))
        
        // Get invitation from Firestore
        const { db } = await import('../config/firebase.js')
        const { doc, getDoc } = await import('firebase/firestore')
        
        const invitationRef = doc(db, 'deviceInvitations', invitationId)
        const invitationDoc = await getDoc(invitationRef)
        
        if (!invitationDoc.exists()) {
          setState(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'Invitation not found. It may have been revoked or expired.',
          }))
          return
        }
        
        const invitation = invitationDoc.data()
        
        // Check invitation status
        if (invitation.status !== 'pending') {
          const statusMessages = {
            'accepted': 'This invitation has already been accepted.',
            'revoked': 'This invitation has been revoked.',
            'expired': 'This invitation has expired.',
          }
          setState(prev => ({ 
            ...prev, 
            loading: false, 
            error: statusMessages[invitation.status] || 'This invitation is no longer valid.',
          }))
          return
        }
        
        // Check if invitation has expired
        if (invitation.expiresAt && invitation.expiresAt.toDate() < new Date()) {
          setState(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'This invitation has expired.',
          }))
          return
        }
        
        // Check if invitation email matches current user
        const normalizedUserEmail = user.email.toLowerCase().trim()
        if (normalizedUserEmail !== invitation.invitedEmail) {
          setState(prev => ({ 
            ...prev, 
            loading: false, 
            error: `This invitation was sent to ${invitation.invitedEmail}. Please sign in with the correct account or contact the person who sent the invitation.`,
          }))
          return
        }
        
        // Get inviter information
        let inviterName = 'Unknown User'
        if (invitation.invitedBy) {
          try {
            const inviterInfo = await getUserInfo(invitation.invitedBy)
            inviterName = inviterInfo?.displayName || inviterInfo?.name || inviterInfo?.email || 'Unknown User'
          } catch (error) {
            console.warn('Failed to get inviter info:', error)
          }
        }
        
        setState(prev => ({ 
          ...prev, 
          invitation, 
          inviterName, 
          loading: false,
        }))
        
      } catch (error) {
        console.error('Error loading invitation:', error)
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Failed to load invitation. Please try again.',
        }))
      }
    }

    loadInvitation()
  }, [invitationId, user, getUserInfo])

  // Handle invitation acceptance
  const handleAcceptInvitation = useCallback(async () => {
    if (!invitationId || !state.invitation) return

    try {
      setState(prev => ({ ...prev, accepting: true, error: null }))
      
      await acceptInvitation(invitationId)
      
      setState(prev => ({ ...prev, accepting: false, success: true }))
      
      // Redirect to dashboard after a brief success message
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 2000)
      
    } catch (error) {
      console.error('Error accepting invitation:', error)
      let errorMessage = 'Failed to accept invitation. Please try again.'
      
      if (error.message?.includes('already has access')) {
        errorMessage = 'You already have access to this device.'
      } else if (error.message?.includes('expired')) {
        errorMessage = 'This invitation has expired.'
      } else if (error.message?.includes('not found')) {
        errorMessage = 'Invitation not found or has been revoked.'
      }
      
      setState(prev => ({ ...prev, accepting: false, error: errorMessage }))
    }
  }, [invitationId, state.invitation, acceptInvitation, navigate])

  const handleDecline = useCallback(() => {
    navigate('/dashboard', { replace: true })
  }, [navigate])

  // Loading state
  if (state.loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <div className="mt-3">
            <h5 className="mb-1">Loading Invitation...</h5>
            <p className="text-muted">Verifying invitation details</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (state.error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">Invitation Problem</h4>
                  <p className="text-muted mb-4">{state.error}</p>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-primary"
                      onClick={() => navigate('/dashboard')}
                    >
                      <i className="bi bi-house me-2"></i>
                      Go to Dashboard
                    </button>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={() => window.location.reload()}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (state.success) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">Invitation Accepted!</h4>
                  <p className="text-muted mb-4">
                    You now have access to <strong>{state.invitation?.deviceId}</strong>. 
                    Redirecting to your dashboard...
                  </p>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Redirecting...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Main invitation acceptance UI
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-lg border-0">
              <div className="card-body p-4">
                {/* Header */}
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <i className="bi bi-envelope-heart text-primary" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-2">Device Invitation</h4>
                  <p className="text-muted">You&apos;ve been invited to access a charging device</p>
                </div>

                {/* Invitation Details */}
                <div className="bg-light rounded p-3 mb-4">
                  <div className="row g-2">
                    <div className="col-sm-4">
                      <small className="text-muted">Invited by:</small>
                      <div className="fw-semibold">{state.inviterName}</div>
                    </div>
                    <div className="col-sm-4">
                      <small className="text-muted">Device:</small>
                      <div className="fw-semibold">{state.invitation?.deviceId}</div>
                    </div>
                    <div className="col-sm-4">
                      <small className="text-muted">Access Level:</small>
                      <div>
                        <span className={`badge ${
                          state.invitation?.permission === 'admin' ? 'bg-danger' : 
                          state.invitation?.permission === 'technician' ? 'bg-warning text-dark' : 'bg-secondary'
                        }`}>
                          {state.invitation?.permission || 'user'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {state.invitation?.message && (
                    <div className="mt-3 pt-3 border-top">
                      <small className="text-muted">Message:</small>
                      <div className="fst-italic">&quot;{state.invitation.message}&quot;</div>
                    </div>
                  )}
                </div>

                {/* Expiration Warning */}
                {state.invitation?.expiresAt && (
                  <div className="alert alert-warning" role="alert">
                    <i className="bi bi-clock me-2"></i>
                    <small>
                      This invitation expires on {state.invitation.expiresAt.toDate().toLocaleDateString()}
                    </small>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  <button 
                    className="btn btn-primary btn-lg"
                    onClick={handleAcceptInvitation}
                    disabled={state.accepting || isLoading}
                  >
                    {state.accepting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Accepting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        Accept Invitation
                      </>
                    )}
                  </button>
                  
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={handleDecline}
                    disabled={state.accepting || isLoading}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    Decline
                  </button>
                </div>

                {/* Help Text */}
                <div className="text-center mt-3">
                  <small className="text-muted">
                    Questions about this invitation? Contact {state.inviterName} or 
                    <a href="mailto:support@revatek.com" className="text-decoration-none ms-1">our support team</a>.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AcceptInvitationPage
